import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { Box, IconCheck, useDialogControllers } from '@machobear/design-system'
import noop from 'lodash/noop'

import { useEnsureLoggedIn } from '@/hooks/use-ensure-loggedIn'
import useRole from '@/hooks/use-role'
import useSessionComplete from '@/hooks/use-session-complete'
import { useShowLandlordErrorToast } from '@/hooks/use-show-landlord-error-toast'
import DSLoginModal from '@/features/Auth/login-modal/ds-login-modal'
import { useIsListingInRecentMessages } from '@/features/Listings/hooks/recent-messages'
import { Button } from '@/components/button-new'
import { ViewListingButtonIcon } from '@/components/icons'

import { useTermsDialogContext } from '../terms-dialog/context'
import DSMessageModal from './ds-message-modal'
import type { Listing } from './types'
import { setListingCardDataLayer } from './utils'

const ControlButton = styled(Button)`
  width: 100%;
  height: 40px;
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  z-index: 10;

  svg {
    color: #8f8f8f;
  }
`

type Props = {
  listing: Listing
  isCustomCompanyPage?: boolean
  loading?: boolean
  customBaseUrl?: string
  messageClickId?: string
  isCardLocked?: boolean
  featured?: boolean
  stackButtons?: boolean
  onComplete?: () => void
}

const getMessageButtonClickId = (
  isLoggedIn: boolean,
  allowApplications: boolean,
  messageClickId: string | undefined
) => {
  const userState = isLoggedIn ? 'logged' : 'listing'
  const clickId = messageClickId ?? 'message-click'
  return allowApplications
    ? `${userState}-${clickId}`
    : `${userState}-blockapp-${clickId}`
}

export const ListingCardActions = ({
  listing,
  isCustomCompanyPage = false,
  loading = false,
  messageClickId,
  isCardLocked = false,
  featured = false,
  stackButtons = false,
  onComplete = noop
}: Props) => {
  const isLoggedIn = useSessionComplete()
  const isLandlord = useRole() === 'landlord'
  const { t } = useTranslation()
  const { visible, showModal, closeModal } = useDialogControllers({
    message: false,
    login: false
  })
  const { showModalForRequiredTerms } = useTermsDialogContext()
  const ensureLoggedIn = useEnsureLoggedIn({
    showLogin: showModal('login')
  })
  const showLandlordErrorToast = useShowLandlordErrorToast()

  const listingId = listing.listing_id
  const allowApplications = listing.allow_applications

  const messageButtonClickId = getMessageButtonClickId(
    isLoggedIn,
    allowApplications,
    messageClickId
  )
  const recentlyMessaged = useIsListingInRecentMessages(listingId)

  const handleMessageClick = (e: Event) => {
    e.preventDefault()
    e.stopPropagation()

    setListingCardDataLayer({
      listing,
      featured,
      eventId: messageButtonClickId
    })

    if (isLandlord) {
      return showLandlordErrorToast()
    }
    isCardLocked
      ? ensureLoggedIn(() =>
          showModalForRequiredTerms('trreb_agreements', () =>
            showModal('message')()
          )
        )
      : showModal('message')()
  }

  return (
    <Box
      display="flex"
      gap="xxsmall"
      flexDirection={stackButtons ? 'column' : 'row'}
      onClick={e => e.stopPropagation()}
    >
      {!isCustomCompanyPage ? (
        <>
          <ControlButton
            theme="secondary"
            size="unset"
            onClick={handleMessageClick}
            id={messageButtonClickId}
            disabled={loading || recentlyMessaged}
          >
            {!featured && recentlyMessaged && (
              <Box
                style={{ width: '14px', height: '14px', marginBottom: '2px' }}
              >
                <IconCheck size="fill" tone="disabled" />
              </Box>
            )}
            {recentlyMessaged
              ? t('b.listing.view.message_sent.button', 'Message Sent')
              : t('b.listing.view.message.button', 'Message')}
          </ControlButton>
        </>
      ) : (
        <ControlButton
          size="unset"
          onClick={() => {}}
          id={`${
            isLoggedIn ? 'logged' : 'listing'
          }-city-page-apply-click-${listingId}`}
          disabled={loading}
        >
          {t('b.listing.view.view_listing.button', 'View listing')}
          <ViewListingButtonIcon height={8} width={8} />
        </ControlButton>
      )}
      <DSMessageModal
        visible={visible.message}
        onClose={closeModal('message')}
        onComplete={() => {
          onComplete()
        }}
        listing={listing}
      />
      <DSLoginModal
        mode="login"
        eventId={'web-signup-submit-savebutton'}
        visible={visible.login}
        onClose={closeModal('login')}
        t={t}
        listingId={parseInt(listingId)}
      />
    </Box>
  )
}
